/**
 * Résolution des IDs d'inscription entre Django (etudiants_bis) et Laravel (délibération, notes).
 *
 * Django expose souvent :
 * - `ins.id_laravel` / `ins.inscription_id` → ID Inscription Laravel (si synchronisé)
 * - `ins.id` → PK inscription Django (souvent = ID Laravel quand pas de champ dédié)
 *
 * Erreur historique : `ins.id || ins.inscription_id || ins.id_laravel` mettait Django en premier
 * et provoquait des collisions entre étudiants (ex. NE…_HP).
 *
 * Erreur inverse : ignorer complètement `ins.id` casse les étudiants sans id_laravel renseigné.
 */

function toPositiveInt(raw: unknown): number | null {
	if (raw === null || raw === undefined || raw === '') return null
	const n = Number(raw)
	return Number.isFinite(n) && n > 0 ? n : null
}

/** ID Laravel à utiliser pour lier une inscription Django ↔ Laravel. */
export function resolveLaravelInscriptionId(source: any): number | null {
	if (!source || typeof source !== 'object') return null

	return (
		toPositiveInt(source.id_laravel)
		?? toPositiveInt(source.inscription_id)
		?? toPositiveInt(source.id)
	)
}

/**
 * Clés à indexer pour une inscription dans une Map inscription_id → données.
 * N'indexe `ins.id` que s'il n'y a pas d'ID Laravel explicite distinct
 * (évite qu'un django id d'un étudiant A écrase l'id_laravel d'un étudiant B).
 */
export function getInscriptionMapKeys(ins: any): number[] {
	if (!ins || typeof ins !== 'object') return []

	const idLaravel = toPositiveInt(ins.id_laravel)
	const inscriptionId = toPositiveInt(ins.inscription_id)
	const djangoId = toPositiveInt(ins.id)

	const keys = new Set<number>()
	if (idLaravel) keys.add(idLaravel)
	if (inscriptionId) keys.add(inscriptionId)

	const hasExplicitLaravel = idLaravel !== null || inscriptionId !== null
	const djangoMatchesExplicit =
		(idLaravel !== null && djangoId === idLaravel)
		|| (inscriptionId !== null && djangoId === inscriptionId)

	if (djangoId !== null && (!hasExplicitLaravel || djangoMatchesExplicit)) {
		keys.add(djangoId)
	}

	return [...keys]
}

/** Indexe des données par inscription_id à partir de la liste etudiants_bis. */
export function buildMapByLaravelInscriptionId<T>(
	etudiantsBis: any[],
	buildEntry: (student: any) => T
): Map<number, T> {
	const map = new Map<number, T>()

	for (const student of etudiantsBis) {
		if (!student) continue
		const entry = buildEntry(student)

		if (Array.isArray(student.inscriptions)) {
			for (const ins of student.inscriptions) {
				for (const key of getInscriptionMapKeys(ins)) {
					const existing = map.get(key)
					if (existing !== undefined && existing !== entry) {
						// En cas de conflit, prioriser l'entrée dont l'ID Laravel explicite correspond à la clé
						const insLaravel = resolveLaravelInscriptionId(ins)
						if (insLaravel === key) {
							map.set(key, entry)
						}
						continue
					}
					map.set(key, entry)
				}
			}
		}
	}

	return map
}

/** Map inscription_id → objet étudiant Django (etudiants_bis). */
export function buildEtudiantsBisByLaravelInscriptionId(etudiantsBis: any[]): Map<number, any> {
	return buildMapByLaravelInscriptionId(etudiantsBis, (student) => student)
}
